import { agruparClientesPorEmpresa } from "./empresaContacto";
import { getPaisCode } from "./getPaisCode";
import { client } from "./supabase";

// Resuelve "empresa tiene un mail SALIENTE atribuible después de tal fecha, POR LADO" para el
// Radar Comercial (estado "escrita" / regla "los dos lados", §14 del diseño) —
// docs/modulos/alertas-recurrencia-comercial.md.
//
// POR QUÉ CONSULTA DIRECTA Y NO EL RESUMEN PRECALCULADO: `crm_actividad_resumen_grupos`
// (fetchResumenGrupos, ya usado por la vía (c) de apagado) tiene grano (cliente_id, lado, tipo,
// día) pero NO guarda dirección del mail — mezcla entrante y saliente, y gmail-sync (verificado en
// supabase/functions/gmail-sync/index.ts) es el único productor de MAIL y sí distingue: guarda
// `metadata.direction` ("outgoing"/"incoming") por mensaje, nunca en una columna top-level (la
// columna `direccion` de `crm_actividades` existe pero está SIEMPRE NULL para gmail — verificado
// con datos reales, 10.28). Extender el resumen precalculado para sumar esa dimensión tocaría una
// tabla e infraestructura compartida con el CRM y Mapa de Calor por un dato que el Radar necesita
// solo, así que la vía elegida es una consulta acotada aparte (más barata y más segura): replica el
// MISMO join de atribución que ya usa la migración `20260920_crm_actividad_resumen_precalculado.sql`
// (contactos relacionales → destinatarios → actividad), con el filtro extra `metadata.direction`.
//
// GMAIL-SYNC SIEMPRE INSERTA `cliente_id: null` (verificado en el código de la function) — el mail
// NUNCA llega con cliente_id propio, así que la única vía de atribución posible es "por
// destinatario" (nunca "directa"): el email del destinatario resuelve a un `usuario_id` de
// `usuarios`, que a su vez es contacto (`clientes_contactos_cliente`/`_agencia`) de uno o más
// `cliente_id` — la tabla de origen (ccc/cca) es también su LADO.
//
// GUARD DE AMBIGÜEDAD (10.270 aplicado a nivel EMPRESA, no cliente_id — 10.276): un contacto de
// agencia real atiende legítimamente varias marcas-fila de la MISMA empresa (ej. Carmelo/Pernod
// Ricard, 19 `cliente_id` → 1 sola empresa — no es ambiguo, 10.276 ya los trata como una unidad)
// pero también hay contactos de agencia que atienden VARIAS EMPRESAS DISTINTAS de verdad (ej.
// Magdalena/Dentsu, 30 `cliente_id` → 22 empresas; medido: 79 contactos así en Chile, 66 en Perú).
// Sin este guard, un mail a Magdalena se atribuía por igual a sus 22 empresas. Un contacto cuyos
// `cliente_id` resuelven a 2+ `empresaNorm` distintos queda EXCLUIDO de la atribución — ninguna de
// sus empresas se marca "escrita" por su intermedio (mismo principio que 10.270). El hallazgo de
// que `crm_actividad_resumen_grupos` NO tiene este guard a ningún nivel se reporta aparte
// (ROADMAP_TECNICO.md) — no se toca en esta sesión.
//
// POR LADO (Commit 3, 2026-09-26): el mismo pool de contactos no-ambiguos con email que resuelve
// `saliente` es, sin ninguna query extra, la fuente de "qué lados tiene la empresa con contacto
// atribuible" (`contactoConEmailPorEmpresa`) — la regla "los dos lados" (§14) la necesita para
// decidir qué lados son EXIGIBLES antes de marcar `escrita`. Deliberadamente NO se reusa
// `radarContactos.js` para esto: esa función resuelve por `cliente_id` (para la lista de
// destinatarios del modal, sin guard de ambigüedad — cualquier contacto de ESE cliente_id es
// válido para escribirle) mientras que acá la certeza de atribución exige el guard — son dos
// preguntas distintas sobre la misma tabla, no una duplicación (10.1).
const DIAS_HISTORIA = 150; // ventana amplia hacia atrás; cubre con margen el encendido más viejo (45 + ~31 días)

const vacio = { saliente: [], contactoConEmailPorEmpresa: [] };

const armarContactoConEmailPorEmpresa = (ladosConContactoPorEmpresa) =>
  [...ladosConContactoPorEmpresa.entries()].map(([empresa_norm, lados]) => ({ empresa_norm, lados: [...lados] }));

export const fetchMailSalienteResumen = async ({ pais, clientes }) => {
  const paisCode = getPaisCode(pais);
  const clienteIds = (clientes || []).map((c) => c.id);
  if (!paisCode || clienteIds.length === 0) return vacio;

  const gruposEmpresa = agruparClientesPorEmpresa(clientes);
  const empresaPorClienteId = new Map();
  for (const [, grupo] of gruposEmpresa) {
    for (const clienteId of grupo.clienteIds) empresaPorClienteId.set(clienteId, grupo.empresaNorm);
  }

  const [{ data: ccc }, { data: cca }] = await Promise.all([
    client.from("clientes_contactos_cliente").select("cliente_id, usuario_id").in("cliente_id", clienteIds),
    client.from("clientes_contactos_agencia").select("cliente_id, usuario_id").in("cliente_id", clienteIds),
  ]);
  const contactos = [
    ...(ccc || []).map((c) => ({ ...c, lado: "cliente" })),
    ...(cca || []).map((c) => ({ ...c, lado: "agencia" })),
  ];
  if (contactos.length === 0) return vacio;

  // Contacto -> set de empresas que atiende (guard de ambigüedad) y set de lados en los que aparece
  // (normalmente uno solo; puede ser los dos si la misma persona figura como contacto de cliente Y
  // de agencia de la MISMA empresa no-ambigua).
  const empresasPorUsuarioId = new Map();
  const ladosPorUsuarioId = new Map();
  for (const c of contactos) {
    const empresaNorm = empresaPorClienteId.get(c.cliente_id);
    if (!empresaNorm) continue;
    if (!empresasPorUsuarioId.has(c.usuario_id)) empresasPorUsuarioId.set(c.usuario_id, new Set());
    empresasPorUsuarioId.get(c.usuario_id).add(empresaNorm);
    if (!ladosPorUsuarioId.has(c.usuario_id)) ladosPorUsuarioId.set(c.usuario_id, new Set());
    ladosPorUsuarioId.get(c.usuario_id).add(c.lado);
  }
  const usuarioIdsNoAmbiguos = new Set(
    [...empresasPorUsuarioId.entries()].filter(([, empresas]) => empresas.size === 1).map(([usuarioId]) => usuarioId)
  );
  if (usuarioIdsNoAmbiguos.size === 0) return vacio;

  const { data: usuarios } = await client
    .from("usuarios")
    .select("id, email")
    .in("id", [...usuarioIdsNoAmbiguos]);
  const usuariosConEmail = (usuarios || []).filter((u) => u.email);
  const usuarioIdPorEmail = new Map(usuariosConEmail.map((u) => [u.email.toLowerCase(), u.id]));

  const ladosConContactoPorEmpresa = new Map(); // empresaNorm -> Set(lado)
  for (const u of usuariosConEmail) {
    const empresas = empresasPorUsuarioId.get(u.id);
    if (!empresas || empresas.size !== 1) continue;
    const [empresaNorm] = empresas;
    const lados = ladosPorUsuarioId.get(u.id) || new Set();
    if (!ladosConContactoPorEmpresa.has(empresaNorm)) ladosConContactoPorEmpresa.set(empresaNorm, new Set());
    for (const lado of lados) ladosConContactoPorEmpresa.get(empresaNorm).add(lado);
  }
  const contactoConEmailPorEmpresa = armarContactoConEmailPorEmpresa(ladosConContactoPorEmpresa);

  if (usuarioIdPorEmail.size === 0) return { saliente: [], contactoConEmailPorEmpresa };

  const desde = new Date();
  desde.setDate(desde.getDate() - DIAS_HISTORIA);

  const { data: actividades } = await client
    .from("crm_actividades")
    .select("id, fecha, metadata")
    .eq("pais", paisCode)
    .eq("tipo", "MAIL")
    .eq("metadata->>direction", "outgoing")
    .gte("fecha", desde.toISOString());
  const actividadesNoMasivas = (actividades || []).filter((a) => !a.metadata?.masivo);
  if (actividadesNoMasivas.length === 0) return { saliente: [], contactoConEmailPorEmpresa };
  const fechaPorActividadId = new Map(actividadesNoMasivas.map((a) => [a.id, a.fecha]));

  const CHUNK = 300;
  const actividadIds = actividadesNoMasivas.map((a) => a.id);
  const lotes = [];
  for (let i = 0; i < actividadIds.length; i += CHUNK) lotes.push(actividadIds.slice(i, i + CHUNK));
  const destinatariosPorLote = await Promise.all(
    lotes.map(async (ids) => {
      const { data } = await client
        .from("crm_actividades_destinatarios")
        .select("actividad_id, email")
        .in("actividad_id", ids);
      return data || [];
    })
  );
  const destinatarios = destinatariosPorLote.flat();

  const ultimaPorClave = new Map(); // `${empresaNorm}|${lado}` -> fecha
  for (const d of destinatarios) {
    const usuarioId = usuarioIdPorEmail.get((d.email || "").toLowerCase());
    if (usuarioId == null || !usuarioIdsNoAmbiguos.has(usuarioId)) continue;
    const [empresaNorm] = empresasPorUsuarioId.get(usuarioId); // set de tamaño 1, ya filtrado
    const fecha = fechaPorActividadId.get(d.actividad_id);
    if (!fecha) continue;
    const lados = ladosPorUsuarioId.get(usuarioId) || new Set();
    for (const lado of lados) {
      const clave = `${empresaNorm}|${lado}`;
      const actual = ultimaPorClave.get(clave);
      if (!actual || fecha > actual) ultimaPorClave.set(clave, fecha);
    }
  }

  const saliente = [...ultimaPorClave.entries()].map(([clave, ultima_fecha]) => {
    const [empresa_norm, lado] = clave.split("|");
    return { empresa_norm, lado, ultima_fecha };
  });

  return { saliente, contactoConEmailPorEmpresa };
};
